// @flow
import type { ActionMeta } from 'react-select';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import withGroupClass from '@hoc/withGroupClass';

import AdminLayout from '@layout/components/AdminLayout';

import AdvancedSelectField from '@shared/AdvancedSelectField/components/AdvancedSelectField';
import Toast from '_common/services/Toast/Toast';
import InputField from '_common/components/input-field/InputField';
import Button, { buttonConstants } from '@shared/Button';
import TextareaField from '_common/components/textarea-field/TextareaField';
import Coaches from '@coach/components/Coaches';
import Periods from '@period/components/Periods';
import Registration from '@groupClass/components/Admin/Edit/Registration';
import Time from '@groupClass/components/Admin/Edit/Time';
import SeasonDates from '@groupClass/components/Admin/Edit/SeasonDates';

import ObjectHelpers from '@helpers/ObjectHelpers';
import GroupClassService from '@groupClass/services/GroupClassService';
import TranslationHelpers from '@helpers/TranslationHelpers';
import { scrollToFirstErrorElement } from '_common/services/CommonUtils';
import { formatAddressToString } from '@shared/Locations/services/AddressUtils';
import DateHelpers from '@helpers/DateHelpers';

import type { Error, Option, ErrorProps } from '@core/types';
import type { GroupClassDetailed, GroupClassStatus } from '@groupClass/types';

import { errorColor } from '@app/constants/styles';
import * as groupClassesConstants from '@app/constants/constants';
import { LOADER_TYPE_PAGE } from '_common/components/loader/constants';
import { ADMIN_HEADER_INFORMATION } from '@layout/constants';
import { ERROR_IS_BLANK } from '@helpers/TranslationHelpers';
import {
  RE_MATCH_PERIOD_AMOUNT_PATTERN,
  RE_MATCH_PERIOD_STARTDATE_PATTERN,
  RE_MATCH_PERIOD_ENDDATE_PATTERN,
  RE_MATCH_COACHES_PATTERN,
} from '@helpers/Regex';
import { WEB_PATHS } from '@app/constants/paths';
import * as moment from 'moment';
import {
  GROUP_CLASS_STATUS_ARCHIVED_COMPLIANT,
  GROUP_CLASS_STATUS_ARCHIVED_DEFAULT_PAYMENT,
  GROUP_CLASS_STATUS_ARCHIVED_NOT_COMPLETED,
  GROUP_CLASS_STATUS_ARCHIVED_TO_REMOVE,
  GROUP_CLASS_STATUS_AVAILABLE,
  GROUP_CLASS_STATUS_SEASON_CLOSED_AWAITING_PAYMENT,
  GROUP_CLASS_STATUS_TO_VALIDATE,
  GROUP_CLASS_STATUSES,
} from '@app/constants/constants';
import RolesHelpers from '@user/helpers/RolesHelpers';
import type { User } from '@user/types';
import AuthService from '@user/services/AuthService';
import type { AuthServiceData } from '@user/services/AuthService';
import ModalChangeTheStatusOfGroupClass from '@groupClass/components/ModalChangeTheStatusOfGroupClass';

const { GROUP_CLASS, PAGE_NOT_FOUND } = WEB_PATHS;

const {
  DESCRIPTION_LENGTH_LIMIT,
  GROUP_CLASS_TYPES,
  GROUP_CLASS_TYPE_SIEL_BLEU,
  GROUP_CLASS_TYPE_BENEFICIARY,
  GROUP_CLASS_TYPE_INTERMEDIATE_WITH_COST,
  GROUP_CLASS_TYPE_SUBSIDIZED_WITH_COST,
  DIFFICULTY_LEVELS,
} = groupClassesConstants;

type Props = {
  groupClass: GroupClassDetailed,
};

export const GroupClassDetailedEdit = (props: Props): React$Node => {
  const { groupClass: initialGroupClass } = props;
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();
  const { classId } = useParams();

  const [ currentUser, setCurrentUser ] = useState<User | null>(AuthService.user);
  const [ groupClass, setGroupClass ] = useState<GroupClassDetailed>(initialGroupClass);
  const [ errors, setErrors ] = useState<Error[]>([]);
  const [ modalErrorsToShow, setModalErrorsToShow ] = useState<string[]>([]);
  const [ isAlreadyEdited, setIsAlreadyEdited ] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  const toggleModal = useCallback(() => setIsModalOpen((prev) => !prev), []);

  useEffect(() => {
    GroupClassService.fetchGroupClassById(classId, language)
      .then(setGroupClass)
      .catch(() => navigate(PAGE_NOT_FOUND));
  }, [classId, language]);

  useEffect(() => {
    if (errors.length > 0) {
      let errorsCopy = ObjectHelpers.deepClone(errors);
      errorsCopy.forEach((error) => {
        if (error.propertyPath === 'address.postalCode')
          error.propertyPath = 'address';
      });
      scrollToFirstErrorElement(document.getElementsByClassName('input-edit-form'), errorsCopy);
    }
  }, [errors]);

  const classTypeOption = useMemo((): Option | null => (
    groupClass?.type
      ? {
        label: t(`groupClasses.groupClass.type.${ groupClass.type }`),
        value: groupClass.type,
      }
      : null
  ), [groupClass, language]);

  const difficultyLevelOption = useMemo((): Option | null => (
    groupClass?.difficultyLevel
      ? {
        label: t(`groupClasses.groupClass.difficulty.${ groupClass.difficultyLevel }`),
        value: groupClass.difficultyLevel,
      }
      : null
  ), [groupClass?.difficultyLevel, language]);

  const dayValue = useMemo((): string => (
    groupClass ? DateHelpers.getDayOfWeek(groupClass.day, language) : ''
  ), [groupClass, language]);

  const activityValue = useMemo((): string => (
    groupClass?.activity?.name || ''
  ), [groupClass]);

  const handleChange = useCallback((event: SyntheticEvent<HTMLInputElement>): void => {
    const newGroupClass = ObjectHelpers.deepClone(groupClass);
    const { name, value } = event.currentTarget;

    newGroupClass[name] = (name === 'startDate' || name === 'endDate') ? new Date(value) : value;

    const newErrors: Error[] = ObjectHelpers.deepClone(errors)
      .filter((error) => error.propertyPath !== name);
    setErrors(newErrors);

    if (name === 'registrationTerms') {
      setIsAlreadyEdited(true);
    }

    setGroupClass(newGroupClass);
  }, [groupClass, errors, setErrors]);

  const handleChangeSelect = useCallback((newValue: Option | null, actionMeta: ActionMeta<Option>): void => {
    const newGroupClass = ObjectHelpers.deepClone(groupClass);
    const { action, name } = actionMeta;
    if (name === 'status') {
      setIsModalOpen(true);
      setPendingStatus(newValue.value);
      return;
    }
    if (action === 'clear' || !newValue) {
      newGroupClass[name] = name === 'difficultyLevel' ? null : '';
    } else {
      newGroupClass[name] = newValue.value;
    }
    newGroupClass[name] = action === 'clear' ? '' : newValue.value;
    setGroupClass(newGroupClass);
  }, [groupClass]);

  const confirmStatusChange = useCallback((e) => {
    e.preventDefault();
    if (pendingStatus) {
      const newGroupClass = ObjectHelpers.deepClone(groupClass);
      newGroupClass.status = pendingStatus;
      setGroupClass(newGroupClass);
      setIsModalOpen(false);
    }
  }, [pendingStatus, groupClass]);

  const classTypeOptions = useMemo((): Option[] => (
    GROUP_CLASS_TYPES.map((classType) => ({
      label: t(`groupClasses.groupClass.type.${ classType }`),
      value: classType,
    }))
  ), [language]);

  const difficultyLevelOptions = useMemo((): Option[] => (
    DIFFICULTY_LEVELS.map((difficultyLevel) => ({
      label: t(`groupClasses.groupClass.difficulty.${ difficultyLevel }`),
      value: difficultyLevel,
    }))
  ), [language]);

  const errorsProps = useMemo((): ErrorProps => {
    let errorsPropsTmp = {};
    errors.forEach((error) => {
      let propertyPath = error.propertyPath;
      if (propertyPath === 'address.postalCode') propertyPath = 'address';
      Object.assign(errorsPropsTmp, {
        [`${ propertyPath }`]: {
          color: 'is-danger',
          borderColor: errorColor,
          labelProps: { className: 'has-text-danger' },
          labelTextAreaProps: {
            className: 'has-text-danger edit-label',
          },
          selectFieldHelperText: { text: t(TranslationHelpers.getCommonErrorKeyByCode(error.code, error.propertyPath)), textColor: 'is-danger' },
          error: {
            text: t(TranslationHelpers.getCommonErrorKeyByCode(error.code, error.propertyPath)),
            textColor: 'is-danger',
          },
        },
      });
    });
    return errorsPropsTmp;
  }, [errors, language]);

  const editGooglePlacesClassName = useMemo((): string => clsx({
    'edit-google-address-autocomplete': true,
    'edit-no-margin-bottom': !!errorsProps['address']?.error,
    'input-edit-form': true,
  }), [errorsProps['address']?.error]);

  const editTextareaFieldClassName = useMemo((): string => clsx({
    'edit-description': true,
    'field-border-error': !!errorsProps.description?.color,
    'input-edit-form description': true,
  }), [errorsProps.description?.color]);

  const handleCancel = useCallback((): void => {
    if (groupClass) {
      navigate(GROUP_CLASS.replace(':classId', String(groupClass.id)));
    }
  }, [groupClass]);

  const cleanError = useCallback((customErrors: Error[]): Error[] => {
    let newErrors = [...customErrors];
    if (groupClass) {
      if (newErrors.findIndex((err) => err.propertyPath === 'noPeriods') !== -1
        && (groupClass.type !== GROUP_CLASS_TYPE_SIEL_BLEU
          && groupClass.type !== GROUP_CLASS_TYPE_BENEFICIARY
          && groupClass.type !== GROUP_CLASS_TYPE_SUBSIDIZED_WITH_COST
          && groupClass.type !== GROUP_CLASS_TYPE_INTERMEDIATE_WITH_COST)) {
        newErrors = newErrors.filter((err) => err.propertyPath !== 'noPeriods');
        groupClass.periods.forEach((period, index) => {
          let amount = String(period.amount);
          if (amount === '') {
            newErrors = [...newErrors, { propertyPath: `periods[${ index }].amount`, code: ERROR_IS_BLANK, message: '' }];
          }
        });
      }

      if (newErrors.findIndex((err) => err.propertyPath === 'noPeriods') !== -1
        && (groupClass.type === GROUP_CLASS_TYPE_SIEL_BLEU
        || groupClass.type === GROUP_CLASS_TYPE_BENEFICIARY
        || groupClass.type === GROUP_CLASS_TYPE_SUBSIDIZED_WITH_COST
        || groupClass.type === GROUP_CLASS_TYPE_INTERMEDIATE_WITH_COST)
        && groupClass.periods.length > 0) {
        newErrors = newErrors.filter((err) => err.propertyPath !== 'noPeriods');
      }

      newErrors.forEach((err: Error) => {
        if (err.propertyPath.match(RE_MATCH_COACHES_PATTERN)) {
          newErrors = newErrors.filter((err) => !err.propertyPath.match(RE_MATCH_COACHES_PATTERN));
          groupClass.coaches.map((coach, index) => {
            if (!coach.id) {
              const error = { propertyPath: `coaches[${ index }].name`, code: ERROR_IS_BLANK, message: '' };
              newErrors = [...newErrors, error];
            }
          });
        }
        if (err.propertyPath.match(RE_MATCH_PERIOD_AMOUNT_PATTERN)) {
          groupClass.periods.forEach((period, index) => {
            let amount = String(period.amount);
            if (amount === '') {
              newErrors = [...newErrors, { propertyPath: `periods[${ index }].amount`, code: ERROR_IS_BLANK, message: '' }];
            }
            if (amount !== '') {
              newErrors = newErrors.filter((err) => err.propertyPath !== `periods[${ index }].amount`);
            }
          });
          const periodNumber = parseInt(err.propertyPath.split('[')[1].split(']')[0], 10);
          if (periodNumber > (groupClass.periods.length - 1)) {
            newErrors = newErrors.filter((error) => error.propertyPath !== err.propertyPath);
          }
        }
        if (err.propertyPath.match(RE_MATCH_PERIOD_ENDDATE_PATTERN)) {
          const periodNumber = parseInt(err.propertyPath.split('[')[1].split(']')[0], 10);
          groupClass.periods.forEach((period, index) => {
            if (period.endDate) newErrors = newErrors.filter((err) => err.propertyPath !== `periods[${ index }].endDate`);
          });
          if (periodNumber > (groupClass.periods.length - 1)) {
            newErrors = newErrors.filter((error) => error.propertyPath !== err.propertyPath);
          }
        }
        if (err.propertyPath.match(RE_MATCH_PERIOD_STARTDATE_PATTERN)) {
          const periodNumber = parseInt(err.propertyPath.split('[')[1].split(']')[0], 10);
          groupClass.periods.forEach((period, index) => {
            if (period.startDate) newErrors = newErrors.filter((err) => err.propertyPath !== `periods[${ index }].startDate`);
          });
          if (periodNumber > (groupClass.periods.length - 1)) {
            newErrors = newErrors.filter((error) => error.propertyPath !== err.propertyPath);
          }
        }
      });

      if (groupClass.endDate) newErrors = newErrors.filter((err) => err.propertyPath !== 'endDate');
      if (groupClass.startDate) newErrors = newErrors.filter((err) => err.propertyPath !== 'startDate');
      if (groupClass.type) newErrors = newErrors.filter((err) => err.propertyPath !== 'type');
    }
    return newErrors;
  }, [groupClass]);

  const validateGroupClass = (groupClass: GroupClassDetailed): { errors: Error[], modalErrors: string[] } => {
    let validationErrors: Error[] = [];
    let modalErrors: string[] = [];
    const { startDate: seasonStartDate, endDate: seasonEndDate, periods } = groupClass;

    // Validate coaches
    groupClass.coaches.forEach((coach, index) => {
      if (!coach.id) {
        validationErrors.push({ propertyPath: `coaches[${ index }].name`, code: ERROR_IS_BLANK, message: '' });
      }
    });

    // Validate required fields
    if (!groupClass.type) {
      validationErrors.push({ propertyPath: 'type', code: ERROR_IS_BLANK, message: '' });
    }
    if (!groupClass.description) {
      validationErrors.push({ propertyPath: 'description', code: ERROR_IS_BLANK, message: '' });
    }

    // Validate payment periods for specific types
    if (
      (groupClass.type === GROUP_CLASS_TYPE_SIEL_BLEU
        || groupClass.type === GROUP_CLASS_TYPE_BENEFICIARY
        || groupClass.type === GROUP_CLASS_TYPE_SUBSIDIZED_WITH_COST
        || groupClass.type === GROUP_CLASS_TYPE_INTERMEDIATE_WITH_COST)
      && periods.length === 0
    ) {
      validationErrors.push({ propertyPath: 'noPeriods', code: ERROR_IS_BLANK, message: '' });
    }

    // Validate periods
    if (periods.length > 0) {
      const firstPeriodStart = periods[0].startDate;
      const lastPeriodEndDate = periods[periods.length - 1].endDate;

      periods.forEach((period, index) => {
        const { startDate, endDate, amount } = period;
        // Validate amount
        if (String(amount) === '') {
          validationErrors.push({ propertyPath: `periods[${ index }].amount`, code: ERROR_IS_BLANK, message: '' });
        }

        // Start date must be before end date
        if (moment(startDate).format('YYYY-MM-DD') > moment(endDate).format('YYYY-MM-DD')) {
          modalErrors.push(t('payment.errors.startBeforeEnd'));
        }

        // End date must be strictly after start date
        if (moment(endDate).format('YYYY-MM-DD') < moment(startDate).format('YYYY-MM-DD')) {
          modalErrors.push(t('payment.errors.endAfterStart'));
        }

        const isLastPeriod = periods.length - 1 === index;
        // Check if the end date of the period is out of the range except the last one
        if (!isLastPeriod && moment(endDate).format('YYYY-MM-DD') > moment(seasonEndDate).format('YYYY-MM-DD')) {
          modalErrors.push(t('payment.errors.endOutOfRange'));
        }
      });

      if (moment(lastPeriodEndDate).format('YYYY-MM-DD') !== moment(seasonEndDate).format('YYYY-MM-DD')) {
        modalErrors.push(t('payment.errors.lastEndsOnSeasonEnd'));
      }
      if (moment(firstPeriodStart).format('YYYY-MM-DD') !== moment(seasonStartDate).format('YYYY-MM-DD')) {
        modalErrors.push(t('payment.errors.firstStartsOnSeasonStart'));
      }
    }

    return {
      errors: cleanError(validationErrors),
      modalErrors,
    };
  };

  const handleUserDataUpdate = (data: AuthServiceData): void => {
    setCurrentUser(data.user);
  };

  useEffect(() => AuthService.onChange(handleUserDataUpdate), []);

  const isCoach = useMemo((): boolean => (
    RolesHelpers.isCoach(currentUser)
  ), [currentUser]);

  const isManager = useMemo((): boolean => (
    RolesHelpers.isManager(currentUser)
  ), [currentUser]);

  const isAdmin = useMemo((): boolean => (
    RolesHelpers.isAdmin(currentUser)
  ), [currentUser]);

  const handleSave = useCallback((e: SyntheticEvent<EventTarget>): void => {
    e.preventDefault();

    if (!groupClass) return;

    const initialCopy = ObjectHelpers.deepClone(initialGroupClass);
    const currentCopy = ObjectHelpers.deepClone(groupClass);
    initialCopy.status = null;
    currentCopy.status = null;

    const statusHasChanged = groupClass.status !== initialGroupClass.status;
    const onlyStatusHasChanged = JSON.stringify(initialCopy) === JSON.stringify(currentCopy);

    let validationErrors: Error[] = [];
    let modalErrors: string[] = [];

    // Ignore validation if status has changed only
    if (!(statusHasChanged && onlyStatusHasChanged)) {
      const result = validateGroupClass(groupClass);
      validationErrors = result.errors;
      modalErrors = result.modalErrors;
    }

    if (validationErrors.length > 0 || modalErrors.length > 0) {
      setErrors(validationErrors);
      setModalErrorsToShow(modalErrors);
      return;
    }

    // If current status of the group class is to validate
    // and it pass all the validation rules, then set status to available
    if (
      !statusHasChanged && initialGroupClass.status === GROUP_CLASS_STATUS_TO_VALIDATE
    ) {
      groupClass.status = GROUP_CLASS_STATUS_AVAILABLE;
    }

    GroupClassService.updateGroupClass(groupClass)
      .then((res) => {
        if (res) {
          Toast.success(t('groupClasses.groupClass.peoples.form.wellUpdated'));
          navigate(GROUP_CLASS.replace(':classId', String(groupClass.id)));
        }
      })
      .catch((err) => {
        if (err) {
          setErrors(err);
        }
      })
      .finally(() => {
        setModalErrorsToShow([]);
      });

  }, [groupClass, language, t, errors, cleanError, initialGroupClass]);

  useEffect(() => {
    if (modalErrorsToShow.length > 0) {
      Toast.error(modalErrorsToShow[0]);
    }
  }, [modalErrorsToShow]);

  const loading = useMemo((): boolean => (
    !groupClass
  ), [groupClass]);

  const addressFormatted = useMemo((): string => (
    groupClass?.address ? formatAddressToString(groupClass.address) : ''
  ), [groupClass]);

  const groupClassStatusOptions = useMemo((): Option[] => (
    GROUP_CLASS_STATUSES.map((status) => ({
      label: t(`groupClasses.groupClass.status.${ status }`),
      value: status,
    }))
  ), [language]);

  const groupClassStatusOption = useMemo((): Option | null => (
    groupClass?.status
      ? {
        label: t(`groupClasses.groupClass.status.${ groupClass.status }`),
        value: groupClass.status,
      }
      : null
  ), [groupClass, language, t]);

  const getDisabledOptions = (): GroupClassStatus[] => {
    const statuses = [
      GROUP_CLASS_STATUS_AVAILABLE,
      GROUP_CLASS_STATUS_ARCHIVED_DEFAULT_PAYMENT,
      GROUP_CLASS_STATUS_ARCHIVED_NOT_COMPLETED,
      GROUP_CLASS_STATUS_ARCHIVED_COMPLIANT,
      GROUP_CLASS_STATUS_TO_VALIDATE,
      GROUP_CLASS_STATUS_SEASON_CLOSED_AWAITING_PAYMENT,
      GROUP_CLASS_STATUS_ARCHIVED_TO_REMOVE,
    ];

    const today = moment().startOf('day');
    const endDate = moment(groupClass.endDate).startOf('day');
    const seasonEnded = endDate.isBefore(today);
    const hasAmountDue = groupClass.periods.some((period) => Number(period.amount) > 0);
    const hasPayment = groupClass.periods.some((period) => period.hasPayment);

    // Admin
    if (isAdmin) {
      // Archivé - défaut de paiement -> Saison fermée - attente de paiement
      if (groupClass.status === GROUP_CLASS_STATUS_ARCHIVED_DEFAULT_PAYMENT) {
        return statuses.filter(
          (status) => status !== GROUP_CLASS_STATUS_SEASON_CLOSED_AWAITING_PAYMENT,
        );
      }

      // Archivé - non complété -> À valider - en attente (if season not closed and not ended)
      if (
        groupClass.status === GROUP_CLASS_STATUS_ARCHIVED_NOT_COMPLETED
        && !seasonEnded
      ) {
        return statuses.filter((status) => status !== GROUP_CLASS_STATUS_TO_VALIDATE);
      }

      // Archivé - à retirer de MCSB -> À valider - en attente (if season not ended)
      if (
        groupClass.status === GROUP_CLASS_STATUS_ARCHIVED_TO_REMOVE
        && !seasonEnded
      ) {
        return statuses.filter(
          (status) => status !== GROUP_CLASS_STATUS_TO_VALIDATE,
        );
      }

      // Cours disponible -> Archivé - à retirer de MCSB (if no amount due and no payment entered)
      if (
        groupClass.status === GROUP_CLASS_STATUS_AVAILABLE
        && !hasAmountDue
        && !hasPayment
      ) {
        return statuses.filter(
          (status) => status !== GROUP_CLASS_STATUS_ARCHIVED_TO_REMOVE,
        );
      }
    }

    //  CP / RD / Admin
    if (isCoach || isManager || isAdmin) {
      // À valider - en attente -> Archivé - à retirer de MCSB
      if (groupClass.status === GROUP_CLASS_STATUS_TO_VALIDATE) {
        return statuses.filter(
          (status) => status !== GROUP_CLASS_STATUS_ARCHIVED_TO_REMOVE,
        );
      }
    }

    return statuses;
  };

  return (
    <AdminLayout
      groupClass={ groupClass }
      activeTab={ ADMIN_HEADER_INFORMATION }
      preventPortraitMode
      isLoading={ loading }
      loaderType={ LOADER_TYPE_PAGE }
      requiresEditAccess
    >
      { groupClass && (
        <div className="container-group-class-detailed container">
          <div className="group-class-detailed">
            <div className="group-class-block-title">
              <div className="group-class-title">
                <h1 className="group-class-detailed-title">
                  { t('groupClasses.groupClass.navigation.infos') }
                  <span>{ t('groupClasses.groupClass.infos') }</span>
                </h1>
              </div>
            </div>
            <div className="group-class-container-white">
              <div className="edit-container">
                <form className="edit-form">
                  <div className="edit-line-buttons-for-mobile">
                    <Button
                      className="edit-cancel"
                      onClick={ handleCancel }
                      type={ buttonConstants.SECONDARY }
                    >
                      { t('common.cancel') }
                    </Button>
                    <Button
                      onClick={ handleSave }
                      type={ buttonConstants.PRIMARY }
                    >
                      { t('common.save') }
                    </Button>
                  </div>
                  <h2 className="edit-title no-margin">
                    { t('groupClasses.groupClass.detailed.general') }
                  </h2>
                  <div className="edit-two-sides">
                    <InputField
                      id="activity.name"
                      name="activity.name"
                      label={ t('groupClasses.groupClass.detailed.activity') }
                      className={ editGooglePlacesClassName }
                      value={ activityValue }
                      isDisabled
                      readOnly
                    />
                    <AdvancedSelectField
                      // Read only, cause this field is configured in Odoo
                      isDisabled={ true }
                      id="type"
                      name="type"
                      label={ t('groupClasses.groupClass.detailed.classType') }
                      options={ classTypeOptions }
                      isClearable
                      color={ errorsProps.type?.color }
                      labelProps={ errorsProps.type?.labelProps }
                      borderColor={ errorsProps.type?.borderColor }
                      helpProps={ errorsProps.type?.selectFieldHelperText }
                      className="edit-field input-edit-form type"
                      value={ classTypeOption }
                      placeholder={ t('groupClasses.groupClass.detailed.types.placeholder') }
                      onChange={ handleChangeSelect }
                      required
                      isSearchable={ false }
                    />
                  </div>
                  <div className="edit-two-sides">
                    <InputField
                      id="groupClassKey"
                      name="groupClassKey"
                      label={ t('groupClasses.groupClass.detailed.groupClassKey') }
                      className="edit-field input-edit-form"
                      value={ groupClass.groupClassKey || '' }
                      isDisabled
                      readOnly
                    />
                    <InputField
                      id="seasonKey"
                      name="seasonKey"
                      label={ t('groupClasses.groupClass.detailed.seasonKey') }
                      className="edit-field input-edit-form"
                      value={ groupClass.seasonKey }
                      isDisabled
                      readOnly
                    />
                  </div>
                  <div className="edit-two-sides">
                    <InputField
                      id="caseNumber"
                      name="caseNumber"
                      label={ t('groupClasses.groupClass.detailed.caseNumber') }
                      className="edit-field input-edit-form"
                      value={ groupClass.caseNumber || '' }
                      isDisabled
                      readOnly
                    />
                    <InputField
                      id="name"
                      name="name"
                      label={ t('groupClasses.groupClass.detailed.courseName') }
                      className="edit-field input-edit-form"
                      value={ groupClass.name }
                      isDisabled
                      readOnly
                    />
                  </div>
                  <div className="edit-two-sides">
                    <InputField
                      id="address"
                      name="address"
                      label={ t('groupClasses.groupClass.detailed.address.address') }
                      className="edit-field input-edit-form"
                      value={ addressFormatted }
                      isDisabled
                      readOnly
                    />
                    <InputField
                      id="address.complementNumber"
                      name="complementNumber"
                      label={ t('groupClasses.groupClass.detailed.complementNumber') }
                      className="edit-field"
                      value={ groupClass.address.complementNumber || '' }
                      isDisabled
                      readOnly
                    />
                  </div>
                  <div className="edit-two-sides">
                    <InputField
                      id="day"
                      name="day"
                      label={ t('groupClasses.groupClass.detailed.day') }
                      className="edit-field"
                      value={ dayValue }
                      isDisabled
                      readOnly
                    />
                    <Time
                      groupClass={ groupClass }
                    />
                  </div>
                  <div className="edit-two-sides">
                    <InputField
                      id="format"
                      name="format"
                      label={ t('groupClasses.groupClass.detailed.format') }
                      className="edit-field input-edit-form"
                      value={ groupClass.isOnline ? t('groupClasses.tag.online') : t('groupClasses.tag.inPerson') }
                      isDisabled
                      readOnly
                    />

                    <AdvancedSelectField
                      id="status"
                      name="status"
                      label={ t('groupClasses.groupClass.detailed.status') }
                      options={ groupClassStatusOptions }
                      color={ errorsProps.status?.color }
                      labelProps={ errorsProps.status?.labelProps }
                      borderColor={ errorsProps.status?.borderColor }
                      helpProps={ errorsProps.status?.selectFieldHelperText }
                      className="edit-field input-edit-form type"
                      value={ groupClassStatusOption }
                      onChange={ handleChangeSelect }
                      required
                      isSearchable={ false }
                      disabledOptions={ getDisabledOptions() }
                    />

                    <ModalChangeTheStatusOfGroupClass
                      isActive={ isModalOpen }
                      toggleModal={ toggleModal }
                      showSuccessMessage={ (msg) => Toast.success(t(msg)) }
                      onClickYes={ confirmStatusChange }
                    />
                  </div>

                  <AdvancedSelectField
                    id="difficultyLevel"
                    name="difficultyLevel"
                    label={ t('groupClasses.groupClass.detailed.difficultyLevel') }
                    options={ difficultyLevelOptions }
                    className="edit-field input-edit-form"
                    value={ difficultyLevelOption }
                    placeholder={ t('groupClasses.groupClass.detailed.difficultyPlaceholder') }
                    onChange={ handleChangeSelect }
                    isClearable
                    isSearchable={ false }
                    color={ errorsProps.difficultyLevel?.color }
                    labelProps={ errorsProps.difficultyLevel?.labelProps }
                    borderColor={ errorsProps.difficultyLevel?.borderColor }
                    helpProps={ errorsProps.difficultyLevel?.selectFieldHelperText }
                  />

                  <TextareaField
                    id="description"
                    className={ editTextareaFieldClassName }
                    classNameTextarea="textarea"
                    name="description"
                    label={ t('common.description') }
                    limitedChar={ DESCRIPTION_LENGTH_LIMIT }
                    color={ errorsProps.description?.color }
                    labelProps={ errorsProps.description?.labelTextAreaProps || { className: 'edit-label' } }
                    helpProps={ errorsProps.description?.error }
                    value={ groupClass.description }
                    onChange={ handleChange }
                    required
                  />

                  <Coaches
                    groupClass={ groupClass }
                    setGroupClass={ setGroupClass }
                    errorsProps={ errorsProps }
                  />

                  <Periods
                    errorsProps={ errorsProps }
                    groupClass={ groupClass }
                    setGroupClass={ setGroupClass }
                  />

                  <SeasonDates
                    errorsProps={ errorsProps }
                    groupClass={ groupClass }
                    setGroupClass={ setGroupClass }
                    onChange={ handleChange }
                  />

                  <Registration
                    onChange={ handleChange }
                    groupClass={ groupClass }
                    errorsProps={ errorsProps }
                    isAlreadyEdited={ isAlreadyEdited }
                  />

                  <div className="edit-line-buttons">
                    <Button
                      className="edit-cancel"
                      onClick={ handleCancel }
                      type={ buttonConstants.SECONDARY }
                    >
                      { t('common.cancel') }
                    </Button>
                    <Button
                      onClick={ handleSave }
                      type={ buttonConstants.PRIMARY }
                    >
                      { t('common.save') }
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      ) }
    </AdminLayout>
  );
};

export default withGroupClass(GroupClassDetailedEdit);
